import { formatDate, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { map, of, race, switchMap, take, timer } from 'rxjs';
import { GROWTHBOOK } from '../growthbook';
import { getFullPath } from '../lib/get-full-path';
import { PicflowMaintenanceService } from '../maintenance';
import { PICFLOW_CONFIG } from '../picflow-config';
import { PICFLOW_STORYBLOK } from '../picflow-storyblok';
import { BrowserService } from './browser.service';
import { LanguageService } from './language.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService implements PicflowMaintenanceService {
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private languageService = inject(LanguageService);
  private toasts = inject(ToastService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private route = inject(ActivatedRoute);
  private browserService = inject(BrowserService);
  private storage = inject(StorageService);
  private config = inject(PICFLOW_CONFIG);
  private growthBook = inject(GROWTHBOOK);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private shownOutages: Set<number> = new Set();

  private checker = this.router.events.subscribe(event => {
    if (event instanceof NavigationEnd) {
      this.checkMaintenance();
    }
  });

  private dateWithTimeZone(
    timeZone: string,
    year: number,
    month: number,
    day: number,
    hour: number,
    minute = 0,
    second = 0
  ) {
    const date = new Date(Date.UTC(year, month, day, hour, minute, second));
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timeZone }));
    const offset = utcDate.getTime() - tzDate.getTime();
    date.setTime(date.getTime() + offset);
    return date;
  }

  // 2024-04-06 05:05
  private convertDateToLocalTime(dateString: string) {
    const entryTime = dateString.split(' ')[1];
    const year = parseInt(dateString.split('-')[0]);
    const month = parseInt(dateString.split('-')[1]) - 1;
    const day = parseInt(dateString.split('-')[2].split(' ')[0]);
    const hour = parseInt(entryTime.split(':')[0]);
    const minute = parseInt(entryTime.split(':')[1]);
    return this.dateWithTimeZone(`Europe/Zurich`, year, month, day, hour, minute);
  }

  public getRealTimeMaintenanceMode() {
    return this.loader.get('maintenance-mode', () => {
      return race([
        this.http
          .get(`${this.browserService.getCorrectPicflowUrl(true)}/internal-api/features`, { transferCache: false })
          .pipe(
            switchMap(async resp => {
              if (!resp) return of(undefined);
              return this.growthBook.setPayload(resp);
            }),
            map(() => {
              return this.growthBook.isOn('maintenance');
            })
          ),
        timer(2000).pipe(
          map(() => {
            console.log('GrowthBook timeout');
            return false;
          })
        ),
      ]);
    });
  }

  public getMaintenance(includeFuture: boolean) {
    return this.storyblokService
      .getStories({ content_type: 'Outage' })
      .pipe(map(resp => resp.stories))
      .pipe(
        map(resp =>
          resp
            ?.map(story => {
              const { from, to, beforeOutageWarning, duringOutageText, showOn, devOnly } = story.content;
              return {
                id: story.id,
                beforeOutageWarning,
                duringOutageText,
                fromDate: from ? this.convertDateToLocalTime(from) : null,
                toDate: to ? this.convertDateToLocalTime(to) : null,
                showOn,
                devOnly,
              };
            })
            .find(outage => {
              if (outage.devOnly && this.config.env === 'production') return false;
              const currentTime = new Date();
              if (!includeFuture) {
                if (outage.fromDate && currentTime < outage.fromDate) return false;
              }
              if (outage.toDate && currentTime > outage.toDate) return false;
              return true;
            })
        )
      );
  }

  private checkMaintenance() {
    if (isPlatformServer(this.platformId)) return;
    this.getMaintenance(true)
      .pipe(take(1))
      .subscribe(outage => {
        if (!outage) return;
        if (isPlatformServer(this.platformId)) return;
        if (this.shownOutages.has(outage.id)) return;
        if (!outage?.beforeOutageWarning) return;
        const storageKey = 'outage-shown_' + outage.id;
        if (this.storage.get(storageKey)) return;
        const context = this.getContext();
        if (!outage.showOn.includes(context)) return;

        if (this.growthBook.isOn('no-maintenance-mode-banner')) return;

        this.shownOutages.add(outage.id);

        this.languageService.loadedLocale$.pipe(take(1)).subscribe(locale => {
          const text = (outage.beforeOutageWarning as string)
            .replace('{{startTime}}', formatDate(outage.fromDate, 'medium', locale))
            .replace('{{endTime}}', formatDate(outage.toDate, 'medium', locale));

          this.toasts.addPreTranslated(text, 'custom', {
            duration: undefined,
            closable: true,
            location: 'head',
            size: 'large',
            icon: '/assets/icons/bell.svg',
            onClose: () => {
              this.storage.set(storageKey, 'true');
            },
          });
        });
      });
  }

  private getContext() {
    const currentUrl = getFullPath(this.route.snapshot);
    if (currentUrl[0] === 'maintenance') return 'maintenance';
    if (this.browserService.isTenantDomain()) return 'galleries';
    if (currentUrl[0] === 'gallery') return 'galleries';
    if (currentUrl[0] === 'a') return 'app';
    if (currentUrl[0] === 'auth') return 'app';
    return 'website';
  }
}
